import React, { useState, useRef, useEffect, useCallback } from 'react'
import ChatMessage from './ChatMessage'
import ChatInput from './ChatInput'
import chatService, { USE_STREAMING } from '../../services/chatService'
import { ChatMessageItem } from './chatTypes'
import {
  CHAT_INTENTS_SHOW_SUGGESTIONS,
  CHAT_POST_LIST_SUGGESTED_QUERIES,
  CHAT_SUGGESTED_QUERIES,
} from './suggestedQueries'
import authStore from '../../store/authStore'

const DEMO_CHAT_ALERT =
  'This feature is available only to Mechasense app users. Sign in with your Mechasense account to access this feature.'

interface ChatBotProps {
  onResponseReady?: () => void
  onNavigate?: () => void
}

function ChatBot({ onResponseReady, onNavigate }: ChatBotProps) {
  const demoCheck = authStore?.currentUser?.active_role?.role_type === -1
  const [messages, setMessages] = useState<ChatMessageItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  // × hides the prompts for this conversation; a new chat (fresh mount) shows them again.
  const [promptsDismissed, setPromptsDismissed] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  // Copilot server-side session id (#5); undefined until the first reply returns one.
  const sessionIdRef = useRef<string | undefined>(undefined)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const lastBotWithIntent = [...messages]
    .reverse()
    .find((m) => m.sender === 'bot' && !m.isError && Boolean(m.intent))

  const showSuggestedQueries =
    messages.length === 0 ||
    (lastBotWithIntent?.intent != null &&
      CHAT_INTENTS_SHOW_SUGGESTIONS.has(lastBotWithIntent.intent))

  const suggestedQueries =
    lastBotWithIntent?.intent === 'LIST_MY_ASSETS'
      ? CHAT_POST_LIST_SUGGESTED_QUERIES
      : CHAT_SUGGESTED_QUERIES

  const handleStop = useCallback(() => {
    abortControllerRef.current?.abort()
  }, [])

  const dismissSuggestedPrompts = () => {
    setPromptsDismissed(true)
  }

  const handleSendMessage = async (messageText: string) => {
    if (demoCheck || !messageText.trim()) return

    const userMessage: ChatMessageItem = {
      text: messageText,
      sender: 'user',
      timestamp: new Date().toISOString(),
    }

    setMessages((prev) => [...prev, userMessage])
    setIsLoading(true)

    const botMessageId = Date.now()
    const botMessage: ChatMessageItem = {
      id: botMessageId,
      text: '',
      sender: 'bot',
      timestamp: new Date().toISOString(),
      pending: true,
    }
    setMessages((prev) => [...prev, botMessage])
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    try {
      const conversationHistory = messages
        .slice(-10)
        .filter((msg) => !msg.isError)
        .map((msg) => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text,
        }))

      if (USE_STREAMING) {
        await chatService.sendMessageStream(
          messageText,
          conversationHistory,
          (chunk: string) => {
            setMessages((prev) => {
              const newMessages = [...prev]
              const messageIndex = newMessages.findIndex((m) => m.id === botMessageId)
              if (messageIndex !== -1) {
                newMessages[messageIndex] = {
                  ...newMessages[messageIndex],
                  text: newMessages[messageIndex].text + chunk,
                  pending: false,
                }
              }
              return newMessages
            })
          },
          (intent: string) => {
            setMessages((prev) => {
              const newMessages = [...prev]
              const messageIndex = newMessages.findIndex((m) => m.id === botMessageId)
              if (messageIndex !== -1) {
                newMessages[messageIndex] = { ...newMessages[messageIndex], intent }
              }
              return newMessages
            })
          },
          signal
        )
      } else {
        const data = await chatService.sendMessage(
          messageText,
          conversationHistory,
          signal,
          sessionIdRef.current
        )
        if (data.session_id) sessionIdRef.current = data.session_id
        setMessages((prev) => {
          const newMessages = [...prev]
          const messageIndex = newMessages.findIndex((m) => m.id === botMessageId)
          if (messageIndex !== -1) {
            newMessages[messageIndex] = {
              ...newMessages[messageIndex],
              text: data.response,
              pending: false,
              ...(data.intent ? { intent: data.intent } : {}),
              ...(data.blocks ? { blocks: data.blocks } : {}),
              ...(data.refs ? { refs: data.refs } : {}),
            }
          }
          return newMessages
        })
      }
      onResponseReady?.()
    } catch (error: unknown) {
      const errMsg =
        error instanceof Error
          ? error.message
          : "I'm unable to complete this request right now due to a temporary system issue. Please try again in a moment. If the issue continues, contact support."
      if (errMsg === '__ABORTED__') {
        setMessages((prev) =>
          prev.map((m) => (m.id === botMessageId ? { ...m, pending: false } : m))
        )
        return
      }
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== botMessageId)
        return [
          ...filtered,
          {
            text: errMsg,
            sender: 'bot',
            timestamp: new Date().toISOString(),
            isError: true,
          },
        ]
      })
    } finally {
      abortControllerRef.current = null
      setIsLoading(false)
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === botMessageId && m.sender === 'bot')
        if (idx === -1) return prev
        const row = prev[idx]
        if (!row.pending) return prev
        const next = [...prev]
        next[idx] = { ...row, pending: false }
        return next
      })
    }
  }

  return (
    <div className="chatbot-container">
      <div className="chatbot-messages">
        {messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💬</div>
            <div className="empty-state-text">Hi! How can I help you today?</div>
            <div className="empty-state-subtext">
              Ask me anything about your equipment, sensors, or data.
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <ChatMessage
                key={message.id ?? `${message.sender}-${message.timestamp}-${index}`}
                message={message}
                onNavigate={onNavigate}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>
      {showSuggestedQueries && !demoCheck && !promptsDismissed && (
        <div className="chat-suggested-queries">
          <div className="chat-suggested-queries-label">
            <span>Suggested Prompts</span>
            <button
              type="button"
              className="chat-suggested-queries-close"
              aria-label="Close suggested prompts"
              onClick={dismissSuggestedPrompts}
            >
              ×
            </button>
          </div>
          <div className="chat-suggested-queries-chips" role="list">
            {suggestedQueries.map((s) => (
              <button
                key={s.label}
                type="button"
                role="listitem"
                className="chat-suggested-chip"
                disabled={isLoading}
                onClick={() => handleSendMessage(s.message)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {demoCheck && (
        <div className="alert alert-warning chat-demo-alert" role="alert">
          {DEMO_CHAT_ALERT}
        </div>
      )}
      <ChatInput
        onSendMessage={handleSendMessage}
        disabled={isLoading || demoCheck}
        isLoading={isLoading}
        onStop={handleStop}
      />
    </div>
  )
}

export default ChatBot